/**
 * Elevation Profile Component
 *
 * Renders an elevation profile chart using uPlot showing:
 * - With matched route: route elevation (filled) + track overlay (red) + waypoints + position
 * - Without route: track-only elevation profile that grows in real-time
 */

import { LitElement, html, css } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import uPlot from 'uplot'
import uPlotCss from 'uplot/dist/uPlot.min.css?inline'
import type { UnitSystem } from '../../utils/units.js'
import type { DistanceTimePoint } from './distance-time-chart.js'

export interface ProfilePoint {
  /** Distance along route/track from start, in meters */
  distance: number
  /** Elevation in meters */
  elevation: number
}

export interface ProfileWaypoint {
  name: string
  /** Distance along route from start, in meters */
  distance: number
  elevation: number
}

/** A named elevation series for multi-profile overlay mode */
export interface NamedProfile {
  name: string
  points: ProfilePoint[]
  color: string
}

/** A rest period expressed as elapsed seconds from start */
export interface RestPeriod {
  start: number
  end: number
}

@customElement('elevation-profile')
export class ElevationProfile extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      position: relative;
    }

    .chart-container {
      width: 100%;
      flex: 1;
      min-height: 0;
    }

    .no-data {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      opacity: 0.5;
      font-size: 13px;
    }

    .cursor-tooltip {
      position: absolute;
      top: 4px;
      right: 8px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      font-size: 11px;
      padding: 2px 6px;
      border-radius: 3px;
      pointer-events: none;
      z-index: 10;
      white-space: nowrap;
    }

    .stats-bar {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      padding: 0 8px;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.7);
      flex-shrink: 0;
      line-height: 1.2;
    }

    .stats-gain {
      color: #4ade80;
    }

    .stats-loss {
      color: #f87171;
    }
  `

  /** Route elevation profile points (distance, elevation) */
  @property({ type: Array })
  routeProfile: ProfilePoint[] = []

  /** Track overlay points — recorded progress so far */
  @property({ type: Array })
  trackProfile: ProfilePoint[] = []

  /** Current position distance along route (meters) */
  @property({ type: Number })
  currentDistance?: number

  /** Waypoints along the route */
  @property({ type: Array })
  waypoints: ProfileWaypoint[] = []

  @property({ type: String })
  unitSystem: UnitSystem = 'imperial'

  /** Multiple named profiles for overlay mode (used in overview/plan views) */
  @property({ type: Array })
  profiles: NamedProfile[] = []

  /** Distance-time data for mapping cursor distance → elapsed time */
  @property({ type: Array })
  distanceTime: DistanceTimePoint[] = []

  /** When true, use elapsed time on the x-axis instead of distance */
  @property({ type: Boolean })
  timeAxis = false

  /** Whether to show the distance/gain/loss stats bar */
  @property({ type: Boolean })
  showStats = true

  /** Rest periods to shade on time-axis charts (elapsed seconds) */
  @property({ type: Array })
  restPeriods: RestPeriod[] = []

  private chart: uPlot | undefined
  private chartEl: HTMLDivElement | undefined
  private uplotStylesInjected = false
  private _syncing = false
  private cursorTooltip = ''
  private selectionTooltip = ''
  private statsDistance = 0
  private statsGain = 0
  private statsLoss = 0
  private chartXs: number[] = []
  private chartYs: (number | null)[] = []
  private chartDistUnit = ''
  private chartElevUnit = ''
  private chartDistXs: number[] = []
  private useTimeAxis = false
  private _syncingZoom = false

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.chart?.destroy()
    this.chart = undefined
  }

  override updated(changed: Map<string, unknown>): void {
    // Re-resolve chart element in case DOM changed (e.g. no-data → chart-container)
    this.chartEl =
      (this.shadowRoot?.querySelector('.chart-container') as HTMLDivElement | null) ?? undefined

    if (
      changed.has('routeProfile') ||
      changed.has('trackProfile') ||
      changed.has('currentDistance') ||
      changed.has('unitSystem') ||
      changed.has('profiles') ||
      changed.has('distanceTime') ||
      changed.has('timeAxis')
    ) {
      this.rebuildChart()
    }
  }

  private get hasRoute(): boolean {
    return this.routeProfile.length >= 2
  }

  private get hasTrack(): boolean {
    return this.trackProfile.length >= 2
  }

  private get hasProfiles(): boolean {
    return this.profiles.some(p => p.points.length >= 2)
  }

  private ensureStyles(): void {
    if (!this.uplotStylesInjected && this.shadowRoot) {
      const style = document.createElement('style')
      style.textContent =
        uPlotCss +
        `\n.u-cursor-x { border-left: 1.5px solid rgba(255,255,255,0.8) !important; }` +
        `\n.u-cursor-pt { display: none !important; }` +
        `\n.u-select { background: rgba(100,160,220,0.3) !important; }`
      this.shadowRoot.appendChild(style)
      this.uplotStylesInjected = true
    }
  }

  /** Linearly interpolate y at xVal in sorted parallel arrays xs, ys */
  private interpolateY(xs: number[], ys: (number | null)[], xVal: number): number | null {
    if (xs.length === 0) return null
    if (xVal <= xs[0]) return ys[0]
    if (xVal >= xs[xs.length - 1]) return ys[xs.length - 1]
    for (let i = 1; i < xs.length; i++) {
      if (xs[i] >= xVal) {
        const y0 = ys[i - 1]
        const y1 = ys[i]
        if (y0 == null || y1 == null) return y0 ?? y1
        const t = (xVal - xs[i - 1]) / (xs[i] - xs[i - 1])
        return y0 + t * (y1 - y0)
      }
    }
    return ys[xs.length - 1]
  }

  private computeElevStats(
    xs: number[],
    ys: (number | null)[],
    xMin: number,
    xMax: number,
    distXs?: number[]
  ): { distance: number; gain: number; loss: number } {
    if (xMin >= xMax || xs.length === 0) return { distance: 0, gain: 0, loss: 0 }

    const points: number[] = []
    const yStart = this.interpolateY(xs, ys, xMin)
    if (yStart != null) points.push(yStart)

    for (let i = 0; i < xs.length; i++) {
      if (xs[i] <= xMin) continue
      if (xs[i] >= xMax) break
      if (ys[i] != null) points.push(ys[i]!)
    }

    const yEnd = this.interpolateY(xs, ys, xMax)
    if (yEnd != null) points.push(yEnd)

    let gain = 0
    let loss = 0
    for (let i = 1; i < points.length; i++) {
      const delta = points[i] - points[i - 1]
      if (delta > 0) gain += delta
      else loss += Math.abs(delta)
    }

    let distance: number
    if (distXs && distXs.length > 0) {
      const dMin = this.interpolateY(xs, distXs as (number | null)[], xMin) ?? 0
      const dMax = this.interpolateY(xs, distXs as (number | null)[], xMax) ?? 0
      distance = dMax - dMin
    } else {
      distance = xMax - xMin
    }

    return { distance, gain, loss }
  }

  private updateVisibleStats(u: uPlot): void {
    const xMin = u.scales.x.min
    const xMax = u.scales.x.max
    if (xMin == null || xMax == null) return
    const distXs = this.useTimeAxis ? this.chartDistXs : undefined
    const stats = this.computeElevStats(this.chartXs, this.chartYs, xMin, xMax, distXs)
    this.statsDistance = stats.distance
    this.statsGain = stats.gain
    this.statsLoss = stats.loss
    this.requestUpdate()
  }

  private updateSelectionTooltip(u: uPlot): void {
    const sel = u.select
    if (sel.width <= 0) {
      this.selectionTooltip = ''
      this.requestUpdate()
      return
    }
    const xMin = u.posToVal(sel.left, 'x')
    const xMax = u.posToVal(sel.left + sel.width, 'x')
    const distXs = this.useTimeAxis ? this.chartDistXs : undefined
    const stats = this.computeElevStats(this.chartXs, this.chartYs, xMin, xMax, distXs)
    const net = stats.gain - stats.loss
    const sign = net >= 0 ? '+' : ''
    this.selectionTooltip = [
      `${stats.distance.toFixed(2)}${this.chartDistUnit}`,
      `↑${Math.round(stats.gain).toLocaleString()}${this.chartElevUnit}`,
      `↓${Math.round(stats.loss).toLocaleString()}${this.chartElevUnit}`,
      `Net: ${sign}${Math.round(net).toLocaleString()}${this.chartElevUnit}`,
    ].join('  ')
    this.requestUpdate()
  }

  private rebuildChart(): void {
    if (!this.chartEl) return

    this.chart?.destroy()
    this.chart = undefined
    this.chartXs = []
    this.chartYs = []
    this.chartDistXs = []
    this.useTimeAxis = false
    this.selectionTooltip = ''

    this.ensureStyles()

    if (this.hasProfiles) {
      this.buildMultiProfileChart()
    } else if (this.hasRoute) {
      this.buildRouteChart()
    } else if (this.hasTrack) {
      this.buildTrackOnlyChart()
    }
  }

  /** Map a distance (meters) to elapsed time (seconds) using distanceTime data */
  private distanceToElapsed(distM: number): number | null {
    if (this.distanceTime.length < 2) return null
    for (let i = 1; i < this.distanceTime.length; i++) {
      if (this.distanceTime[i].distance >= distM) {
        const prev = this.distanceTime[i - 1]
        const curr = this.distanceTime[i]
        const dRange = curr.distance - prev.distance
        if (dRange === 0) return prev.elapsed
        const t = (distM - prev.distance) / dRange
        return prev.elapsed + t * (curr.elapsed - prev.elapsed)
      }
    }
    return this.distanceTime[this.distanceTime.length - 1].elapsed
  }

  /** Map elapsed time (seconds) to distance (meters) using distanceTime data */
  private elapsedToDistance(elapsed: number): number | null {
    if (this.distanceTime.length < 2) return null
    for (let i = 1; i < this.distanceTime.length; i++) {
      if (this.distanceTime[i].elapsed >= elapsed) {
        const prev = this.distanceTime[i - 1]
        const curr = this.distanceTime[i]
        const tRange = curr.elapsed - prev.elapsed
        if (tRange === 0) return prev.distance
        const t = (elapsed - prev.elapsed) / tRange
        return prev.distance + t * (curr.distance - prev.distance)
      }
    }
    return this.distanceTime[this.distanceTime.length - 1].distance
  }

  /** Programmatically position cursor at a given elapsed time (for cross-chart sync) */
  setCursorElapsed(elapsed: number | null): void {
    if (!this.chart) return
    this._syncing = true
    if (elapsed == null) {
      this.chart.setCursor({ left: -10, top: -10 })
    } else if (this.useTimeAxis) {
      const left = this.chart.valToPos(elapsed, 'x')
      this.chart.setCursor({ left, top: -10 })
    } else {
      const distM = this.elapsedToDistance(elapsed)
      if (distM == null) {
        this._syncing = false
        return
      }
      const distFactor = this.unitSystem === 'metric' ? 0.001 : 0.000621371
      const left = this.chart.valToPos(distM * distFactor, 'x')
      this.chart.setCursor({ left, top: -10 })
    }
    this._syncing = false
  }

  /** Programmatically set the x-axis zoom range. Values are always in elapsed seconds. */
  setZoomRange(min: number | null, max: number | null): void {
    if (!this.chart) return
    this._syncingZoom = true
    if (min == null || max == null) {
      this.chart.setScale('x', {
        min: this.chart.data[0][0],
        max: this.chart.data[0][this.chart.data[0].length - 1],
      })
    } else if (this.useTimeAxis) {
      this.chart.setScale('x', { min, max })
    } else {
      const distFactor = this.unitSystem === 'metric' ? 0.001 : 0.000621371
      const minDist = this.elapsedToDistance(min)
      const maxDist = this.elapsedToDistance(max)
      if (minDist != null && maxDist != null) {
        this.chart.setScale('x', { min: minDist * distFactor, max: maxDist * distFactor })
      }
    }
    this._syncingZoom = false
  }

  /** Emit chart-zoom with elapsed-second values regardless of x-axis mode */
  private emitZoom(u: uPlot): void {
    if (this._syncingZoom) return
    let min = u.scales.x.min
    let max = u.scales.x.max
    if (min == null || max == null) return
    if (!this.useTimeAxis) {
      const distFactor = this.unitSystem === 'metric' ? 0.001 : 0.000621371
      min = this.distanceToElapsed(min / distFactor) ?? min
      max = this.distanceToElapsed(max / distFactor) ?? max
    }
    this.dispatchEvent(
      new CustomEvent('chart-zoom', {
        detail: { min, max },
        bubbles: true,
        composed: true,
      })
    )
  }

  private emitCursor(
    distDisplayUnit: number,
    distFactor: number,
    elevDisplayUnit: number,
    distUnit: string,
    elevUnit: string
  ): void {
    const distM = distDisplayUnit / distFactor
    const elapsed = this.distanceToElapsed(distM)
    this.cursorTooltip = `${distDisplayUnit.toFixed(2)}${distUnit}  ${Math.round(elevDisplayUnit)}${elevUnit}`
    this.requestUpdate()
    if (!this._syncing) {
      this.dispatchEvent(
        new CustomEvent('chart-cursor', {
          detail: { elapsed },
          bubbles: true,
          composed: true,
        })
      )
    }
  }

  private clearCursor(): void {
    this.cursorTooltip = ''
    this.requestUpdate()
    if (!this._syncing) {
      this.dispatchEvent(
        new CustomEvent('chart-cursor', {
          detail: { elapsed: null },
          bubbles: true,
          composed: true,
        })
      )
    }
  }

  private formatElapsed(s: number): string {
    const m = Math.floor(s / 60)
    const h = Math.floor(m / 60)
    if (h > 0) return `${h}h${String(m % 60).padStart(2, '0')}m`
    return `${m}m${String(Math.floor(s) % 60).padStart(2, '0')}s`
  }

  /**
   * Multi-profile overlay mode: multiple named series on the same chart.
   * Each profile uses its own distance-from-start on the X axis.
   * uPlot requires aligned X data, so we merge all X values into a single
   * sorted array and interpolate each series onto that shared grid.
   */
  private buildMultiProfileChart(): void {
    if (!this.chartEl) return

    const validProfiles = this.profiles.filter(p => p.points.length >= 2)
    if (validProfiles.length === 0) return

    const isMetric = this.unitSystem === 'metric'
    const distFactor = isMetric ? 0.001 : 0.000621371
    const elevFactor = isMetric ? 1 : 3.28084
    const distUnit = isMetric ? 'km' : 'mi'
    const elevUnit = isMetric ? 'm' : 'ft'

    const useTime = this.timeAxis && this.distanceTime.length >= 2

    // Build per-profile distance/elevation arrays in display units
    const profileData = validProfiles.map(p => {
      const dists = p.points.map(pt => pt.distance * distFactor)
      const times = useTime ? p.points.map(pt => this.distanceToElapsed(pt.distance) ?? 0) : dists
      return {
        name: p.name,
        color: p.color,
        distances: dists,
        xVals: times,
        elevations: p.points.map(pt => pt.elevation * elevFactor),
      }
    })

    // Merge all X values into a single sorted array
    const allX = new Set<number>()
    for (const pd of profileData) {
      for (const x of pd.xVals) allX.add(x)
    }
    const xs = [...allX].sort((a, b) => a - b)

    // For each profile, interpolate elevation at every X point.
    const seriesData: (number | null)[][] = profileData.map(pd => {
      const minX = pd.xVals[0]
      const maxX = pd.xVals[pd.xVals.length - 1]
      return xs.map(x => {
        if (x < minX || x > maxX) return null
        return this.interpolateY(pd.xVals, pd.elevations, x)
      })
    })

    const opts = this.buildOpts(distUnit, elevUnit, useTime)

    opts.series = [
      {}, // x-axis placeholder
      ...profileData.map(pd => ({
        label: pd.name,
        stroke: pd.color,
        fill: ElevationProfile.colorWithAlpha(pd.color, 0.1),
        width: 2,
        spanGaps: false,
      })),
    ]

    // Cursor hook for multi-profile: show all series values at hover
    opts.hooks = opts.hooks ?? {}
    opts.hooks.setCursor = [
      (u: uPlot) => {
        const left = u.cursor.left
        if (left == null || left < 0) {
          this.clearCursor()
          return
        }
        const xVal = u.posToVal(left, 'x')
        const parts: string[] = useTime
          ? [this.formatElapsed(xVal)]
          : [`${xVal.toFixed(2)}${distUnit}`]
        for (let i = 0; i < profileData.length; i++) {
          const yVal = this.interpolateY(profileData[i].xVals, profileData[i].elevations, xVal)
          if (yVal != null) {
            parts.push(`${profileData[i].name}: ${Math.round(yVal)}${elevUnit}`)
          }
        }
        this.cursorTooltip = parts.join('  ')
        this.requestUpdate()
        if (!this._syncing) {
          let elapsed: number | null
          if (useTime) {
            elapsed = xVal
          } else {
            const distM = xVal / distFactor
            elapsed = this.distanceToElapsed(distM)
          }
          this.dispatchEvent(
            new CustomEvent('chart-cursor', {
              detail: { elapsed },
              bubbles: true,
              composed: true,
            })
          )
        }
      },
    ]
    opts.hooks.setSelect = [(u: uPlot) => this.updateSelectionTooltip(u)]
    opts.hooks.setScale = [
      (u: uPlot, key: string) => {
        if (key === 'x') {
          this.updateVisibleStats(u)
          this.emitZoom(u)
        }
      },
    ]
    if (useTime) opts.hooks.draw = [(u: uPlot) => this.drawRestShading(u)]

    this.chartXs = xs
    this.chartYs = seriesData[0]
    this.chartDistUnit = distUnit
    this.chartElevUnit = elevUnit
    this.useTimeAxis = useTime
    if (useTime) {
      const pd0 = profileData[0]
      this.chartDistXs = xs.map(x => this.interpolateY(pd0.xVals, pd0.distances, x) ?? 0)
    }

    const data: uPlot.AlignedData = [xs, ...seriesData] as uPlot.AlignedData
    this.chart = new uPlot(opts, data, this.chartEl)
    this.setupTouchCursor()
  }

  /**
   * Route mode: route profile (filled area) + track overlay + waypoints + current position.
   */
  private buildRouteChart(): void {
    if (!this.chartEl) return

    const isMetric = this.unitSystem === 'metric'
    const distFactor = isMetric ? 0.001 : 0.000621371
    const elevFactor = isMetric ? 1 : 3.28084
    const distUnit = isMetric ? 'km' : 'mi'
    const elevUnit = isMetric ? 'm' : 'ft'

    const distances = this.routeProfile.map(p => p.distance * distFactor)
    const routeElev = this.routeProfile.map(p => p.elevation * elevFactor)

    // Track overlay: align to route distances
    const trackElev: (number | null)[] = new Array(distances.length).fill(null)
    if (this.trackProfile.length > 0) {
      let ti = 0
      for (let ri = 0; ri < this.routeProfile.length && ti < this.trackProfile.length; ri++) {
        const rd = this.routeProfile[ri].distance
        while (ti < this.trackProfile.length - 1 && this.trackProfile[ti + 1].distance <= rd) {
          ti++
        }
        if (Math.abs(this.trackProfile[ti].distance - rd) < 50) {
          trackElev[ri] = this.trackProfile[ti].elevation * elevFactor
        }
      }
    }

    // Current position index
    let currentIdx: number | undefined
    if (this.currentDistance !== undefined) {
      let minDiff = Infinity
      for (let i = 0; i < this.routeProfile.length; i++) {
        const diff = Math.abs(this.routeProfile[i].distance - this.currentDistance)
        if (diff < minDiff) {
          minDiff = diff
          currentIdx = i
        }
      }
    }

    const waypoints = this.waypoints

    const opts = this.buildOpts(distUnit, elevUnit, false, u => {
      const ctx = u.ctx
      // Waypoint markers
      for (const wp of waypoints) {
        const wpDist = wp.distance * distFactor
        const px = u.valToPos(wpDist, 'x', true)
        if (px < u.bbox.left || px > u.bbox.left + u.bbox.width) continue
        ctx.save()
        ctx.strokeStyle = 'rgba(255,255,255,0.4)'
        ctx.lineWidth = 1
        ctx.setLineDash([3, 3])
        ctx.beginPath()
        ctx.moveTo(px, u.bbox.top)
        ctx.lineTo(px, u.bbox.top + u.bbox.height)
        ctx.stroke()
        ctx.fillStyle = 'rgba(255,255,255,0.7)'
        ctx.font = '9px sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText(wp.name, px, u.bbox.top - 3)
        ctx.restore()
      }
      // Current position dot
      if (currentIdx !== undefined) {
        const cx = u.valToPos(distances[currentIdx], 'x', true)
        const cy = u.valToPos(routeElev[currentIdx], 'y', true)
        ctx.save()
        ctx.fillStyle = '#22c55e'
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.arc(cx, cy, 5, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
        ctx.restore()
      }
    })

    opts.series = [
      {},
      {
        label: 'Route',
        stroke: 'rgba(100,160,220,0.8)',
        fill: 'rgba(100,160,220,0.15)',
        width: 1.5,
      },
      { label: 'Track', stroke: '#ff4444', width: 2 },
    ]

    // Add cursor hook — interpolate between data points
    opts.hooks = opts.hooks ?? {}
    opts.hooks.setCursor = [
      (u: uPlot) => {
        const left = u.cursor.left
        if (left == null || left < 0) {
          this.clearCursor()
          return
        }
        const xVal = u.posToVal(left, 'x')
        const yVal = this.interpolateY(distances, routeElev, xVal)
        if (yVal == null) {
          this.clearCursor()
          return
        }
        this.emitCursor(xVal, distFactor, yVal, distUnit, elevUnit)
      },
    ]
    opts.hooks.setSelect = [(u: uPlot) => this.updateSelectionTooltip(u)]
    opts.hooks.setScale = [
      (u: uPlot, key: string) => {
        if (key === 'x') this.updateVisibleStats(u)
      },
    ]

    this.chartXs = distances
    this.chartYs = routeElev
    this.chartDistUnit = distUnit
    this.chartElevUnit = elevUnit

    const data: uPlot.AlignedData = [distances, routeElev, trackElev as number[]]
    this.chart = new uPlot(opts, data, this.chartEl)
    this.setupTouchCursor()
  }

  /**
   * Track-only mode: show the recorded track elevation as a growing filled area.
   */
  private buildTrackOnlyChart(): void {
    if (!this.chartEl) return

    const isMetric = this.unitSystem === 'metric'
    const distFactor = isMetric ? 0.001 : 0.000621371
    const elevFactor = isMetric ? 1 : 3.28084
    const distUnit = isMetric ? 'km' : 'mi'
    const elevUnit = isMetric ? 'm' : 'ft'
    const useTime = this.timeAxis && this.distanceTime.length >= 2

    const distances = this.trackProfile.map(p => p.distance * distFactor)
    const elevations = this.trackProfile.map(p => p.elevation * elevFactor)
    const xs = useTime
      ? this.trackProfile.map(p => this.distanceToElapsed(p.distance) ?? 0)
      : distances

    const opts = this.buildOpts(distUnit, elevUnit, useTime)

    opts.series = [
      {},
      { label: 'Elevation', stroke: '#ff4444', fill: 'rgba(255,68,68,0.15)', width: 2 },
    ]

    opts.hooks = opts.hooks ?? {}
    opts.hooks.setCursor = [
      (u: uPlot) => {
        const left = u.cursor.left
        if (left == null || left < 0) {
          this.clearCursor()
          return
        }
        const xVal = u.posToVal(left, 'x')
        const yVal = this.interpolateY(xs, elevations, xVal)
        if (yVal == null) {
          this.clearCursor()
          return
        }
        if (useTime) {
          this.cursorTooltip = `${this.formatElapsed(xVal)}  ${Math.round(yVal)}${elevUnit}`
          this.requestUpdate()
          if (!this._syncing) {
            this.dispatchEvent(
              new CustomEvent('chart-cursor', {
                detail: { elapsed: xVal },
                bubbles: true,
                composed: true,
              })
            )
          }
        } else {
          this.emitCursor(xVal, distFactor, yVal, distUnit, elevUnit)
        }
      },
    ]
    opts.hooks.setSelect = [(u: uPlot) => this.updateSelectionTooltip(u)]
    opts.hooks.setScale = [
      (u: uPlot, key: string) => {
        if (key === 'x') {
          this.updateVisibleStats(u)
          this.emitZoom(u)
        }
      },
    ]
    if (useTime) opts.hooks.draw = [(u: uPlot) => this.drawRestShading(u)]

    this.chartXs = xs
    this.chartYs = elevations
    this.chartDistUnit = distUnit
    this.chartElevUnit = elevUnit
    this.useTimeAxis = useTime
    if (useTime) this.chartDistXs = distances

    const data: uPlot.AlignedData = [xs, elevations]
    this.chart = new uPlot(opts, data, this.chartEl)
    this.setupTouchCursor()
  }

  /** Wire touch events to uPlot cursor and long-press drag-to-zoom */
  private setupTouchCursor(): void {
    if (!this.chart) return
    const chart = this.chart
    const el = chart.root.querySelector('.u-over')
    if (!el) return
    const wrap = el as HTMLElement

    const LONG_PRESS_MS = 400
    const MOVE_THRESHOLD = 8
    let pressTimer: ReturnType<typeof setTimeout> | null = null
    let isDragging = false
    let dragStartLeft = 0
    let startX = 0
    let startY = 0
    let lastTap = 0

    wrap.addEventListener(
      'touchstart',
      (e: TouchEvent) => {
        const touch = e.touches[0]
        const rect = wrap.getBoundingClientRect()
        startX = touch.clientX
        startY = touch.clientY
        const left = touch.clientX - rect.left
        const top = touch.clientY - rect.top

        // Double-tap to reset zoom
        const now = Date.now()
        if (now - lastTap < 300) {
          e.preventDefault()
          lastTap = 0
          chart.setScale('x', {
            min: chart.data[0][0],
            max: chart.data[0][chart.data[0].length - 1],
          })
          return
        }
        lastTap = now

        isDragging = false
        pressTimer = setTimeout(() => {
          isDragging = true
          dragStartLeft = left
          chart.setSelect({ left, width: 0, top: 0, height: chart.over.clientHeight }, false)
          try {
            const cap = (window as unknown as Record<string, unknown>).Capacitor as
              | { Plugins?: { Haptics?: { impact: (opts: { style: string }) => void } } }
              | undefined
            cap?.Plugins?.Haptics?.impact({ style: 'medium' })
          } catch {
            // Haptics not available on web
          }
        }, LONG_PRESS_MS)

        chart.setCursor({ left, top }, true)
      },
      { passive: false }
    )

    wrap.addEventListener(
      'touchmove',
      (e: TouchEvent) => {
        const touch = e.touches[0]
        const rect = wrap.getBoundingClientRect()
        const left = touch.clientX - rect.left
        const top = touch.clientY - rect.top

        if (
          pressTimer &&
          (Math.abs(touch.clientX - startX) > MOVE_THRESHOLD ||
            Math.abs(touch.clientY - startY) > MOVE_THRESHOLD)
        ) {
          clearTimeout(pressTimer)
          pressTimer = null
        }

        if (isDragging) {
          e.preventDefault()
          const selLeft = Math.min(dragStartLeft, left)
          const selWidth = Math.abs(left - dragStartLeft)
          chart.setSelect(
            { left: selLeft, width: selWidth, top: 0, height: chart.over.clientHeight },
            false
          )
        } else {
          e.preventDefault()
          chart.setCursor({ left, top }, true)
        }
      },
      { passive: false }
    )

    wrap.addEventListener('touchend', () => {
      if (pressTimer) {
        clearTimeout(pressTimer)
        pressTimer = null
      }
      if (isDragging) {
        const sel = chart.select
        if (sel.width > 10) {
          const xMin = chart.posToVal(sel.left, 'x')
          const xMax = chart.posToVal(sel.left + sel.width, 'x')
          chart.setScale('x', { min: xMin, max: xMax })
        }
        chart.setSelect({ left: 0, width: 0, top: 0, height: 0 }, false)
        isDragging = false
      }
      this.clearCursor()
    })
  }

  /** Draw translucent shading for rest periods on a time-axis chart */
  private drawRestShading(u: uPlot): void {
    if (this.restPeriods.length === 0) return
    const ctx = u.ctx
    ctx.save()
    ctx.fillStyle = 'rgba(255,200,0,0.12)'
    for (const rp of this.restPeriods) {
      const x0 = u.valToPos(rp.start, 'x', true)
      const x1 = u.valToPos(rp.end, 'x', true)
      if (x1 < u.bbox.left || x0 > u.bbox.left + u.bbox.width) continue
      const left = Math.max(x0, u.bbox.left)
      const right = Math.min(x1, u.bbox.left + u.bbox.width)
      ctx.fillRect(left, u.bbox.top, right - left, u.bbox.height)
    }
    ctx.restore()
  }

  private buildOpts(
    distUnit: string,
    elevUnit: string,
    useTime?: boolean,
    drawHook?: (u: uPlot) => void
  ): uPlot.Options {
    const hooks: uPlot.Options['hooks'] = drawHook ? { draw: [drawHook] } : {}
    const xAxisValues: uPlot.Axis.Values = useTime
      ? (_u: uPlot, vals: number[]) =>
          vals.map(v => {
            const m = Math.floor(v / 60)
            const h = Math.floor(m / 60)
            if (h > 0) return `${h}h${String(m % 60).padStart(2, '0')}m`
            return `${m}m`
          })
      : (_u: uPlot, vals: number[]) => vals.map(v => `${v.toFixed(1)}${distUnit}`)
    return {
      width: this.chartEl!.clientWidth,
      height: this.chartEl!.clientHeight,
      cursor: {
        show: true,
        x: true,
        y: false,
        drag: { x: true, y: false, setScale: true },
      },
      legend: { show: false },
      axes: [
        {
          stroke: 'white',
          grid: { stroke: 'rgba(255,255,255,0.3)', width: 1 },
          ticks: { stroke: 'rgba(255,255,255,0.5)', width: 1 },
          font: '10px sans-serif',
          space: 60,
          values: xAxisValues,
        },
        {
          stroke: 'white',
          grid: { stroke: 'rgba(255,255,255,0.3)', width: 1 },
          ticks: { stroke: 'rgba(255,255,255,0.5)', width: 1 },
          font: '10px sans-serif',
          size: 55,
          space: 40,
          values: (_u: uPlot, vals: number[]) => vals.map(v => `${Math.round(v)}${elevUnit}`),
        },
      ],
      scales: { x: { auto: true }, y: { auto: true } },
      series: [],
      hooks,
    }
  }

  /** Convert any CSS color (hex or rgb) to rgba with the given alpha */
  private static colorWithAlpha(color: string, alpha: number): string {
    if (color.startsWith('#')) {
      const hex = color.slice(1)
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return `rgba(${r},${g},${b},${alpha})`
    }
    // rgb(...) → rgba(..., alpha)
    return color.replace(')', `,${alpha})`).replace('rgb', 'rgba')
  }

  private resizeObserver?: ResizeObserver

  override firstUpdated(): void {
    this.chartEl =
      (this.shadowRoot?.querySelector('.chart-container') as HTMLDivElement | null) ?? undefined
    if (!this.chartEl) return

    this.resizeObserver = new ResizeObserver(() => {
      if (this.chart && this.chartEl) {
        this.chart.setSize({
          width: this.chartEl.clientWidth,
          height: this.chartEl.clientHeight,
        })
      }
    })
    this.resizeObserver.observe(this.chartEl)

    this.rebuildChart()
  }

  override render(): unknown {
    const tooltip = this.selectionTooltip || this.cursorTooltip
    const hasData = this.hasRoute || this.hasTrack || this.hasProfiles
    return html`${this.showStats && this.chartXs.length > 0
        ? html`<div class="stats-bar">
            <span>${this.statsDistance.toFixed(2)}${this.chartDistUnit}</span>
            <span class="stats-gain"
              >↑${Math.round(this.statsGain).toLocaleString()}${this.chartElevUnit}</span
            >
            <span class="stats-loss"
              >↓${Math.round(this.statsLoss).toLocaleString()}${this.chartElevUnit}</span
            >
          </div>`
        : ''}
      <div class="chart-container">
        ${!hasData ? html`<div class="no-data">No elevation data yet</div>` : ''}
      </div>
      ${tooltip ? html`<div class="cursor-tooltip">${tooltip}</div>` : ''}`
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'elevation-profile': ElevationProfile
  }
}
